/**
 * API client for the OMNΛS intelligence surface:
 * requirements register, award workflow (leveling / recommendation / EITL),
 * execution intelligence, scheduling agent, and construction scheduler.
 *
 * Every one of these routes is feature-flagged server-side and returns
 * 503 {detail:{code:"not_enabled"}} when off. classifyWorkflowError() turns
 * that (and the rest of the backend's error vocabulary) into a typed shape
 * the UI can render as a first-class state instead of a generic failure.
 */
import apiClient from './api';

/**
 * Normalize an axios error from the intelligence surface.
 *
 * The implementation lives in lib/workflowErrors.js — dependency-free so the
 * test harness can drive the whole refusal vocabulary directly — and is
 * re-exported here because every existing caller imports it from this
 * module.
 */
export { classifyWorkflowError } from '../lib/workflowErrors';
import { classifyWorkflowError as classify } from '../lib/workflowErrors';

// ---------------------------------------------------------------------------
// Requirements register — contract "requirements-register-1"
// ---------------------------------------------------------------------------

export const requirementsApi = {
  /** params: { requirementType?, status?, hardGatesOnly? } */
  listForProject: async (projectId, params = {}) => {
    const { data } = await apiClient.get(`/requirements/projects/${projectId}`, { params });
    return data;
  },

  get: async (requirementId) => {
    const { data } = await apiClient.get(`/requirements/${requirementId}`);
    return data;
  },

  gates: async (projectId) => {
    const { data } = await apiClient.get(`/requirements/projects/${projectId}/gates`);
    return data;
  },

  scheduleImpact: async (requirementId) => {
    const { data } = await apiClient.get(`/requirements/${requirementId}/schedule-impact`);
    return data;
  },

  /**
   * Raise verification with evidence. body:
   * { verificationStatus: DOCUMENT_CITED|HUMAN_VERIFIED|CONFLICTING,
   *   sourceDocumentId?, sourceQuote?, sourcePage?, sourceSection?,
   *   evidenceConfidence?, note?, confirmed: true }
   *
   * NOTE: the requirements request schemas are extra="forbid" and declare no
   * contractVersion field (unlike award-workflow/scheduling-agent), so none
   * is sent — an extra key would 422 the whole request.
   */
  verify: async (requirementId, body) => {
    const { data } = await apiClient.post(`/requirements/${requirementId}/verify`, {
      confirmed: true,
      ...body,
    });
    return data;
  },

  supersede: async (requirementId, body) => {
    const { data } = await apiClient.post(`/requirements/${requirementId}/supersede`, {
      confirmed: true,
      ...body,
    });
    return data;
  },

  update: async (requirementId, body) => {
    const { data } = await apiClient.patch(`/requirements/${requirementId}`, body);
    return data;
  },

  /**
   * Record the actual submission of an obligation's deliverable.
   * body: { submittedDate? (never future), submittalDocumentIds?, note? }.
   * IDENTIFIED/PREPARING → SUBMITTED; RETURNED_FOR_REVISION/REJECTED →
   * RESUBMITTED. Capability: requirements.submit.
   */
  submit: async (requirementId, body = {}) => {
    const { data } = await apiClient.post(`/requirements/${requirementId}/submit`, body);
    return data;
  },

  /**
   * Record the reviewer's returned decision.
   * body: { decision: APPROVED|APPROVED_AS_NOTED|REJECTED|RETURNED_FOR_REVISION,
   *         returnedDate?, conditions?, note? (≥10 chars for negative outcomes) }.
   * Capability: requirements.review.
   */
  review: async (requirementId, body) => {
    const { data } = await apiClient.post(`/requirements/${requirementId}/review`, {
      confirmed: true,
      ...body,
    });
    return data;
  },

  /**
   * Record obligations (bulk wrapper; idempotent on requirement key).
   * Operator-entered rows begin UNVERIFIED unless they carry a citation —
   * the server downgrades over-claimed evidence and reports the adjustment.
   */
  ingest: async (projectId, requirements) => {
    const { data } = await apiClient.post(`/requirements/projects/${projectId}/ingest`, {
      requirements,
    });
    return data;
  },
};

// ---------------------------------------------------------------------------
// RFQ operating API — contract "rfq-api-1"
// The user-facing quote lifecycle over the canonical BidPackage/BidQuote
// records: record, submit, revise (append-only), withdraw, clarify.
// ---------------------------------------------------------------------------

export const rfqApi = {
  listPackages: async (params = {}) => {
    const { data } = await apiClient.get('/rfq/packages', { params });
    return data;
  },

  getPackage: async (packageId) => {
    const { data } = await apiClient.get(`/rfq/packages/${packageId}`);
    return data;
  },

  candidates: async (packageId) => {
    const { data } = await apiClient.get(`/rfq/packages/${packageId}/candidates`);
    return data;
  },

  listQuotes: async (packageId) => {
    const { data } = await apiClient.get(`/rfq/packages/${packageId}/quotes`);
    return data;
  },

  getQuote: async (quoteId) => {
    const { data } = await apiClient.get(`/rfq/quotes/${quoteId}`);
    return data;
  },

  /** Record revision 1 of one bidder's response (DRAFT, or SUBMITTED with submit:true). */
  createQuote: async (packageId, body) => {
    const { data } = await apiClient.post(`/rfq/packages/${packageId}/quotes`, {
      contractVersion: 'rfq-api-1',
      ...body,
    });
    return data;
  },

  submitQuote: async (quoteId, body = {}) => {
    const { data } = await apiClient.post(`/rfq/quotes/${quoteId}/submit`, {
      contractVersion: 'rfq-api-1',
      ...body,
    });
    return data;
  },

  /** A full restated offer that supersedes the current revision. */
  reviseQuote: async (quoteId, body) => {
    const { data } = await apiClient.post(`/rfq/quotes/${quoteId}/revise`, {
      contractVersion: 'rfq-api-1',
      ...body,
    });
    return data;
  },

  withdrawQuote: async (quoteId, body = {}) => {
    const { data } = await apiClient.post(`/rfq/quotes/${quoteId}/withdraw`, {
      contractVersion: 'rfq-api-1',
      ...body,
    });
    return data;
  },

  requestClarification: async (quoteId, question) => {
    const { data } = await apiClient.post(`/rfq/quotes/${quoteId}/request-clarification`, {
      contractVersion: 'rfq-api-1',
      question,
    });
    return data;
  },
};

// ---------------------------------------------------------------------------
// Award workflow — contract "award-workflow-1"
// Leveling and proposals are read/propose only; nothing here ever awards.
// ---------------------------------------------------------------------------

export const awardWorkflowApi = {
  /** Level current quotes. Persists nothing. 409 no_quotes when package has none. */
  level: async (packageId, body = {}) => {
    const { data } = await apiClient.post(`/award-workflow/packages/${packageId}/level`, {
      contractVersion: 'award-workflow-1',
      requirements: [],
      adjustments: [],
      ...body,
    });
    return data;
  },

  /** Calculate + persist an award recommendation (never an award). */
  propose: async (packageId, body = {}) => {
    const { data } = await apiClient.post(`/award-workflow/packages/${packageId}/propose`, {
      contractVersion: 'award-workflow-1',
      requirements: [],
      adjustments: [],
      ...body,
    });
    return data;
  },

  listProposals: async (packageId) => {
    const { data } = await apiClient.get(`/award-workflow/packages/${packageId}/proposals`);
    return data;
  },

  getProposal: async (packageId, proposalId) => {
    const { data } = await apiClient.get(
      `/award-workflow/packages/${packageId}/proposals/${proposalId}`
    );
    return data;
  },

  /**
   * Record the human EITL decision.
   * body: { proposalId, decision: APPROVE|REJECT|REQUEST_REVISION|REQUEST_CLARIFICATION,
   *         note? (required ≥10 chars for everything except APPROVE) }
   */
  decide: async (packageId, body) => {
    const { data } = await apiClient.post(
      `/award-workflow/packages/${packageId}/proposals/decision`,
      { contractVersion: 'award-workflow-1', confirmed: true, ...body }
    );
    return data;
  },

  /**
   * The second, separate human decision: authorize execution handoff for an
   * APPROVED proposal. Links the selected provider into canonical
   * procurement/execution truth. It does NOT execute a subcontract, issue a
   * PO, authorize payment, or notify anyone — the response enumerates each.
   * 409 stale_award blocks a stale authorization; replays are idempotent.
   * Capability: award.authorize.
   */
  authorizeExecution: async (packageId, body) => {
    const { data } = await apiClient.post(
      `/award-workflow/packages/${packageId}/proposals/authorize-execution`,
      { contractVersion: 'award-workflow-1', confirmed: true, ...body }
    );
    return data;
  },
};

// ---------------------------------------------------------------------------
// Execution intelligence — read-only, contracts "schedule-linkage-1"
// and "material-authority-1"
// ---------------------------------------------------------------------------

export const executionIntelligenceApi = {
  projectActivities: async (projectId) => {
    const { data } = await apiClient.get(
      `/execution-intelligence/projects/${projectId}/activities`
    );
    return data;
  },

  activity: async (activityId) => {
    const { data } = await apiClient.get(`/execution-intelligence/activities/${activityId}`);
    return data;
  },

  materialAuthority: async (projectId, { conflictsOnly = false } = {}) => {
    const { data } = await apiClient.get(
      `/execution-intelligence/projects/${projectId}/material-authority`,
      { params: conflictsOnly ? { conflictsOnly: true } : {} }
    );
    return data;
  },

  deliveryAuthority: async (materialStatusId) => {
    const { data } = await apiClient.get(
      `/execution-intelligence/materials/${materialStatusId}/delivery-authority`
    );
    return data;
  },

  /**
   * Canonical workface classification — contract "what-can-start-2".
   * Statuses: CAN_START_NOW | CAN_START_CONDITIONALLY | BLOCKED |
   * IN_PROGRESS | COMPLETE | UNKNOWN. Deterministic arithmetic over gates;
   * nothing here is AI-determined and nothing needs confirmation.
   */
  whatCanStart: async (projectId) => {
    const { data } = await apiClient.get(
      `/execution-intelligence/projects/${projectId}/what-can-start`
    );
    return data;
  },

  /**
   * OMNI Flow Director — contract "flow-director-1". What is executable now,
   * in the charter's band vocabulary, plus what has been recorded as
   * changing.
   *
   * It RE-EXPRESSES the classification /what-can-start already produces; it
   * never re-classifies, so it cannot disagree with that lane. Fronts arrive
   * ordered by instanceId alone — a total order with no interpretive content.
   * Callers render the order they receive; re-sorting installs a ranking the
   * contract deliberately refuses to carry, and the gating engine's readiness
   * score is not in this payload at all.
   *
   * `recordedTransitions` is LAYERED: the rows are the ones /telemetry
   * serves, so they are populated only for a caller who also holds
   * telemetry.read. For anyone else the key states its absence rather than
   * vanishing — render that statement, never an empty lane.
   */
  projectFlow: async (projectId) => {
    const { data } = await apiClient.get(
      `/execution-intelligence/projects/${projectId}/flow`
    );
    return data;
  },

  /**
   * Constraint leverage — contract "constraint-leverage-1". Which constraints
   * hold the most work, and what resolving each one actually buys.
   *
   * The response is ALREADY ORDERED by the contract's own fixed rule and the
   * contract calls that attention, not priority. Callers render the order
   * they receive and must not re-sort it into a ranking.
   */
  unlockLeverage: async (projectId) => {
    const { data } = await apiClient.get(
      `/execution-intelligence/projects/${projectId}/unlock-leverage`
    );
    return data;
  },

  /**
   * Counterfactual simulator — contract "counterfactual-execution-1".
   * WRITE-SHAPED, WRITES NOTHING: a POST because the scenario is a body, not
   * because anything is persisted. There is no apply path and an assumption
   * carrying `apply` or `persist` is refused permanently.
   *
   * Needs `execution.simulate`; a PROVIDER_SELECTED scenario additionally
   * needs `rfq.read`, because quote and vendor identity sit behind that
   * capability everywhere else and this surface does not reopen the door.
   *
   * At most 32 assumptions, at most one PROVIDER_SELECTED — the caller sends
   * one scenario per question rather than batching several futures into one.
   */
  counterfactual: async (projectId, assumptions) => {
    const { data } = await apiClient.post(
      `/execution-intelligence/projects/${projectId}/counterfactual`,
      { assumptions }
    );
    return data;
  },

  /**
   * Work packages — contract "executable-work-package-1". Composes execution
   * structure, the embedded blocking set, provider lineage and material
   * answers per package.
   *
   * Availability is stricter than its siblings: flag AND the
   * execution_intelligence entitlement AND the `rfq.read` CAPABILITY, because
   * this is the one runtime surface that joins commercial identity to
   * execution structure. The provider-free questions stay on /what-can-start
   * and /unlock-leverage, which any authenticated tenant user may read.
   */
  workPackages: async (projectId) => {
    const { data } = await apiClient.get(
      `/execution-intelligence/projects/${projectId}/work-packages`
    );
    return data;
  },

  /**
   * Project realism — contract "project-realism-1". Read-only; the approved
   * baseline is never modified by this assessment.
   */
  realism: async (projectId) => {
    const { data } = await apiClient.get(`/execution-intelligence/projects/${projectId}/realism`);
    return data;
  },

  /**
   * Pilot telemetry — contract "pilot-telemetry-1" (backend #22).
   *
   * Read-only, deterministic, and ALL-TIME: there is no date filter in any of
   * its queries, so `dataDate` is when the snapshot was taken and never the
   * bound of a window.
   *
   * Availability note worth knowing before this is placed on a screen: the
   * endpoint carries the `execution_intelligence` entitlement from its router
   * but NO role gate at all — it uses bare `get_current_user`. Every
   * authenticated member of an entitled tenant can read the commercial funnel
   * counts, so the FRONTEND is what keeps this off an external provider's
   * screen. The backend will not do it.
   */
  pilotTelemetry: async (projectId) => {
    const { data } = await apiClient.get(
      `/execution-intelligence/projects/${projectId}/telemetry`
    );
    return data;
  },

  /**
   * Run the deterministic chain now: readiness → ERS → CPM → realism.
   * Refreshes derived intelligence; it does NOT apply schedule changes.
   * Roles: admin/pm/superintendent. May return {skipped:true, reason} for
   * oversized projects — surface that honestly.
   */
  recalculate: async (projectId) => {
    const { data } = await apiClient.post(
      `/execution-intelligence/projects/${projectId}/recalculate`
    );
    return data;
  },
};

// ---------------------------------------------------------------------------
// Scheduling agent — contract "sa-phase-2"
// ---------------------------------------------------------------------------

export const schedulingAgentApi = {
  /** Readiness + duration + earliest-realistic-start for one instance. */
  evaluate: async (instanceId, body = {}) => {
    const { data } = await apiClient.post(
      `/scheduling-agent/instances/${instanceId}/evaluate`,
      { contractVersion: 'sa-phase-2', ...body }
    );
    return data;
  },

  /** Execution CPM over the project. Read-only. 409 no_execution_schedule when empty. */
  analyze: async (projectId, body = {}) => {
    const { data } = await apiClient.post(`/scheduling-agent/projects/${projectId}/analyze`, {
      contractVersion: 'sa-phase-2',
      ...body,
    });
    return data;
  },

  proposeResequence: async (projectId, body = {}) => {
    const { data } = await apiClient.post(
      `/scheduling-agent/projects/${projectId}/resequence-proposal`,
      { contractVersion: 'sa-phase-2', ...body }
    );
    return data;
  },

  listProposals: async (projectId) => {
    const { data } = await apiClient.get(`/scheduling-agent/projects/${projectId}/proposals`);
    return data;
  },

  getProposal: async (projectId, proposalId) => {
    const { data } = await apiClient.get(
      `/scheduling-agent/projects/${projectId}/proposals/${proposalId}`
    );
    return data;
  },

  /** body: { proposalId, decision: APPROVE|REJECT, note? }. Never applies dates. */
  decide: async (projectId, body) => {
    const { data } = await apiClient.post(
      `/scheduling-agent/projects/${projectId}/proposals/decision`,
      { contractVersion: 'sa-phase-2', confirmed: true, ...body }
    );
    return data;
  },
};

// ---------------------------------------------------------------------------
// Construction scheduler — pure CPM calculation, contract "construction-scheduler-1"
// ---------------------------------------------------------------------------

export const constructionSchedulerApi = {
  calculate: async (body) => {
    const { data } = await apiClient.post('/construction-scheduler/calculate', {
      contractVersion: 'construction-scheduler-1',
      ...body,
    });
    return data;
  },
};

const intelligenceService = {
  classifyWorkflowError: classify,
  requirementsApi,
  awardWorkflowApi,
  executionIntelligenceApi,
  schedulingAgentApi,
  constructionSchedulerApi,
  rfqApi,
};

export default intelligenceService;

// ---------------------------------------------------------------------------
// Decision episodes — read-only, contract "decision-episode-1"
// ---------------------------------------------------------------------------

/**
 * The immutable learning unit: what the machine held, what a human added,
 * what they committed to, and what reality later showed — frozen at the
 * decision moment and never refreshed with current project state.
 *
 * Availability: OMNAS_DECISION_EPISODE_API_ENABLED AND the
 * execution_intelligence entitlement AND the `decision.episode.read`
 * capability, which exists because episodes can carry commercial identity and
 * relationship prose.
 *
 * `surface` and `decisionRef` are the only filters the contract offers. There
 * is deliberately no package filter, so a caller asking about one package
 * must say so rather than implying the list is package-scoped.
 */
export const decisionEpisodeApi = {
  episodes: async (projectId, { surface, decisionRef } = {}) => {
    const params = {};
    if (surface) params.surface = surface;
    if (decisionRef) params.decisionRef = decisionRef;
    const { data } = await apiClient.get(
      `/decision-episodes/projects/${projectId}/episodes`,
      { params }
    );
    return data;
  },
};

// ---------------------------------------------------------------------------
// Field execution — contract "execution-observation-1"
// ---------------------------------------------------------------------------

/**
 * The graph's sensors. Observations are ALWAYS STORED: the POST validates
 * shape only, never readiness, status, gates or authority state.
 *
 * START_WORK is deliberately separate. It is a governed transition that may
 * refuse, needs its own `execution.command` capability, and requires
 * `confirmed: true` — a 428 otherwise. Recording what you see and moving the
 * work forward are different permissions.
 */
export const fieldObservationApi = {
  recordInstance: async (instanceId, body) => {
    const { data } = await apiClient.post(
      `/execution/instances/${instanceId}/observations`,
      body
    );
    return data;
  },

  recordMaterial: async (materialId, body) => {
    const { data } = await apiClient.post(
      `/execution/materials/${materialId}/observations`,
      body
    );
    return data;
  },

  recordConstraint: async (projectId, body) => {
    const { data } = await apiClient.post(`/execution/projects/${projectId}/constraints`, body);
    return data;
  },

  /** The governed command. `confirmed` is required; the backend 428s without it. */
  startWork: async (instanceId, { confirmed, startedOn } = {}) => {
    const { data } = await apiClient.post(`/execution/instances/${instanceId}/start-work`, {
      confirmed: confirmed === true,
      ...(startedOn ? { startedOn } : {}),
    });
    return data;
  },

  observations: async (projectId) => {
    const { data } = await apiClient.get(`/execution/projects/${projectId}/observations`);
    return data;
  },
};

/**
 * PRECONSTRUCTION CONTROL PLANE — contract `precon-control-plane-1`.
 *
 * Two acts, deliberately asymmetric in who may perform them:
 *
 *   ANALYZE  any authenticated user. Reads only, writes nothing, and returns
 *            a freshly computed proposal with its own proposalId.
 *   COMMIT   admin / project_manager / pm / superintendent only. Records one
 *            human approval of one specific, freshly recomputed proposal.
 *
 * Availability for both: `OMNAS_PRECON_CONTROL_PLANE_ENABLED` (503
 * `not_enabled`). There is no entitlement on this router. The project guard
 * runs BEFORE the flag check, so an unknown or cross-tenant project answers
 * 404 rather than revealing whether the feature is on.
 *
 * The commit refuses three ways, and none of them is a transport failure:
 *   428 `confirmation_required` — `confirmed` was not true
 *   409 `stale_proposal`        — the project moved; carries currentProposalId
 *   409 `proposal_not_ready`    — carries the analysis's own blockers
 *
 * `applied` is `Literal[False]` in the response schema on this contract
 * version: the commit records a decision and changes no activity, planned
 * date, procurement item, bid package or marketplace post. It says which
 * writes it deferred and what blocks each.
 */
/** Pinned by the request schema: the backend rejects any other value. */
export const PRECON_CONTROL_PLANE_CONTRACT = 'precon-control-plane-1';

export const preconControlPlaneApi = {
  analyze: async (projectId) => {
    const { data } = await apiClient.post(`/precon-control-plane/analyze/${projectId}`);
    return data;
  },

  /**
   * `confirmed` is never defaulted to true here. The caller must pass it, and
   * the backend 428s without it — that refusal is a governed workflow state,
   * not an error to retry around.
   */
  commit: async (projectId, { proposalId, confirmed, approvedByName, note } = {}) => {
    const { data } = await apiClient.post(`/precon-control-plane/commit/${projectId}`, {
      contractVersion: PRECON_CONTROL_PLANE_CONTRACT,
      proposalId,
      confirmed: confirmed === true,
      ...(approvedByName ? { approvedByName } : {}),
      ...(note ? { note } : {}),
    });
    return data;
  },
};
